// src/encounter/lib/booked-visit-type.ts
//
// THE VISIT'S TYPE — the encounter-template `visitType` axis (TS-4, PROD-2820 phase 1).
//
// Phase 1 the axis is the appointment TYPE CODE Scheduling stores on every appointment
// (`appointmentType.code`: ROUTINE = "First Time", FOLLOWUP, WALKIN). The app also sends the booked
// appointment PROFILE id (`appointmentProfile.referenceId`) beside it: PracticeManagement ignores it in
// phase 1 and, in phase 2, resolves it to a practice-defined visit type ("annual review") — so the app
// ships once. Mirrors `booked-specialty.ts`: ONE rule, TWO callers (the list tap → `toEncounterNav`,
// and the page's own GetById → `useAppointmentStatus`), so the two paths cannot drift.
//
// WHERE THE PROFILE ID COMES FROM (cross-model review amendment P2). List rows are NOT proven to carry
// `appointmentProfile.referenceId`, so the page falls back to its GetById read for it — the template
// request is held until that read settles whenever tap-time nav lacks either fact.

type VisitTypeView = {
  appointmentType?: { code?: unknown } | null;
  appointmentProfile?: { referenceId?: unknown } | null;
};

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

/** Only a non-empty string is a visit-type code. QuestionBank owns normalisation and validation. */
export function asVisitTypeCode(v: unknown): string | undefined {
  return typeof v === 'string' && v.trim() ? v.trim() : undefined;
}

/** Only a non-empty string that is not the all-zero GUID is a profile id. */
export function asAppointmentProfileId(v: unknown): string | undefined {
  return typeof v === 'string' && v.trim() && v.trim() !== EMPTY_GUID ? v.trim() : undefined;
}

/** The booked visit type of an appointment (a list row or the GetById payload). */
export function bookedVisitType(appointment: unknown): { visitType?: string; appointmentProfileId?: string } {
  const a = (appointment ?? {}) as VisitTypeView;
  const visitType = asVisitTypeCode(a.appointmentType?.code);
  const appointmentProfileId = asAppointmentProfileId(a.appointmentProfile?.referenceId);
  return {
    ...(visitType ? { visitType } : {}),
    ...(appointmentProfileId ? { appointmentProfileId } : {}),
  };
}

/** The template request's visit type, and whether it is still being looked up. */
export interface TemplateVisitType {
  visitType?: string;
  appointmentProfileId?: string;
  /** True while the GetById fallback is in flight — the template request must wait for it. */
  pending: boolean;
  /**
   * True when the request goes out WITHOUT a visit type because the lookup FAILED (the appointment read
   * errored), not because the visit has none — so the page can say the template was chosen without the
   * visit's type (contract G11), exactly like the specialty notice.
   */
  lookupFailed?: boolean;
}

/**
 * Which visit type (and profile id) the encounter-template request carries.
 *
 *  - Tap-time nav has BOTH → use them, never wait.
 *  - Otherwise wait while the page's GetById is loading (PROD-2608 pins the FIRST template a note is
 *    saved on, so firing without the visit type and correcting later would stick); once it settles,
 *    take each fact from nav first, then from the appointment.
 *  - A FAILED read with no visit type from nav is flagged (`lookupFailed`); a missing profile id alone
 *    is not (phase 1 ignores it).
 *
 * `isLoading` (TanStack: first fetch in flight) is what holds; a disabled query never holds.
 */
export function resolveTemplateVisitType(
  nav: { appointmentTypeCode?: string; appointmentProfileId?: string } | null | undefined,
  appointment: {
    data?: { appointmentTypeCode?: string | null; appointmentProfileId?: string | null } | null;
    isLoading?: boolean;
    isError?: boolean;
  },
): TemplateVisitType {
  const navVisitType = asVisitTypeCode(nav?.appointmentTypeCode);
  const navProfileId = asAppointmentProfileId(nav?.appointmentProfileId);
  if (navVisitType && navProfileId) {
    return { visitType: navVisitType, appointmentProfileId: navProfileId, pending: false };
  }
  if (appointment.isLoading) return { pending: true };

  const visitType = navVisitType ?? asVisitTypeCode(appointment.data?.appointmentTypeCode);
  const appointmentProfileId = navProfileId ?? asAppointmentProfileId(appointment.data?.appointmentProfileId);
  return {
    ...(visitType ? { visitType } : {}),
    ...(appointmentProfileId ? { appointmentProfileId } : {}),
    pending: false,
    ...(!visitType && appointment.isError ? { lookupFailed: true } : {}),
  };
}
